import { createSlice } from '@reduxjs/toolkit'
import { getLobbyGameTablesThunk, getGamesListThunk } from 'redux-thunk/thunk/lobbyGameManagement.thunk'

const normalizeSeatCount = (value) => {
  const count = Number(value)
  return Number.isFinite(count) ? Math.max(0, count) : null
}

const resolveApiSeatCount = (table) => {
  if (table?.seatsOccupied !== undefined && table?.seatsOccupied !== null) {
    const normalized = normalizeSeatCount(table.seatsOccupied)
    if (normalized !== null) return normalized
  }
  if (!table?.tableSeats) return 0
  return table.tableSeats.filter(
    (seat) => seat.userId !== null && seat.sittingOut === false
  ).length
}

const initialState = {
  gameTables: [],
  games: [],
  gamesLoaded: false,
  pendingSeatUpdatesByTableId: {},
  loading: false,
  error: null
}

const lobbySlice = createSlice({
  name: 'lobby',
  initialState,
  reducers: {
    updateTableSeatOccupied: (state, action) => {
      const { tableId, seatsOccupied } = action.payload
      if (tableId == null) return

      const normalized = normalizeSeatCount(seatsOccupied)
      if (normalized === null) return

      const key = String(tableId)
      const updatedAt = Date.now()
      const tableIndex = state.gameTables.findIndex(
        (table) =>
          String(table.id) === key || String(table.tableId) === key
      )

      if (tableIndex !== -1) {
        state.gameTables[tableIndex].seatsOccupied = normalized
        state.gameTables[tableIndex].seatsOccupiedLiveAt = updatedAt
      } else {
        state.pendingSeatUpdatesByTableId[key] = {
          seatsOccupied: normalized,
          updatedAt
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGamesListThunk.pending, (state) => {
        state.gamesLoaded = false
      })
      .addCase(getGamesListThunk.fulfilled, (state, action) => {
        state.games = action.payload ?? []
        state.gamesLoaded = true
      })
      .addCase(getGamesListThunk.rejected, (state) => {
        state.gamesLoaded = true
      })
      .addCase(getLobbyGameTablesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getLobbyGameTablesThunk.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        const fetchStartedAt = action.payload?.fetchStartedAt ?? 0
        const data = action.payload?.data ?? action.payload
        const rows = Array.isArray(data) ? data : (data?.tables ?? data?.rows ?? [])
        const prevById = new Map(
          state.gameTables.map((table) => [
            String(table.id ?? table.tableId),
            table
          ])
        )
        const pending = { ...state.pendingSeatUpdatesByTableId }

        state.gameTables = rows.map((table) => {
          const key = String(table.id ?? table.tableId)
          const prev = prevById.get(key)
          const pendingUpdate = pending[key]

          let seatsOccupied = resolveApiSeatCount(table)
          let seatsOccupiedLiveAt = prev?.seatsOccupiedLiveAt

          if (pendingUpdate) {
            seatsOccupied = pendingUpdate.seatsOccupied
            seatsOccupiedLiveAt = pendingUpdate.updatedAt
            delete pending[key]
          } else if (
            prev?.seatsOccupiedLiveAt != null &&
            prev.seatsOccupiedLiveAt > fetchStartedAt
          ) {
            seatsOccupied = prev.seatsOccupied
            seatsOccupiedLiveAt = prev.seatsOccupiedLiveAt
          }

          return {
            ...table,
            seatsOccupied,
            ...(seatsOccupiedLiveAt != null ? { seatsOccupiedLiveAt } : {})
          }
        })

        state.pendingSeatUpdatesByTableId = pending
      })
      .addCase(getLobbyGameTablesThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? action.error?.message ?? 'Failed to fetch tables'
      })
  }
})

export const { updateTableSeatOccupied } = lobbySlice.actions
export default lobbySlice.reducer
